/**
 * HistoricalViewBanner
 *
 * Amber alert banner shown when the user is viewing a historical revision
 * (not the current live budget). Provides three actions:
 *   - Compare with Current
 *   - Restore as New Revision
 *   - Return to Current
 */

import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { History } from 'lucide-react';
import type { BudgetRevision } from '@/types/budget.types';

interface HistoricalViewBannerProps {
  revision: BudgetRevision;
  onCompareWithCurrent: () => void;
  onRestoreAsRevision: () => void;
  onReturnToCurrent: () => void;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getCategoryLabel(revision: BudgetRevision): string {
  if (revision.reasonCategoryLabel) return revision.reasonCategoryLabel;
  if (revision.reasonCategory) {
    return revision.reasonCategory
      .split('-')
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return '';
}

export function HistoricalViewBanner({
  revision,
  onCompareWithCurrent,
  onRestoreAsRevision,
  onReturnToCurrent,
}: HistoricalViewBannerProps) {
  const versionLabel = `RB-v${revision.revisionNumber}`;
  const dateLabel = formatDate(revision.date);
  const authorLabel = revision.changedByName ?? 'Unknown';
  const categoryLabel = getCategoryLabel(revision);

  const detailParts = [dateLabel, authorLabel, categoryLabel].filter(Boolean);

  return (
    <Alert variant="warning" showIcon={false}>
      <div className="flex items-start gap-3 w-full">
        <History className="h-5 w-5 shrink-0 mt-0.5 text-[var(--accent-border)]" />

        <div className="flex-1 min-w-0">
          <AlertTitle>
            You are viewing {versionLabel}
          </AlertTitle>
          <AlertDescription className="mt-0.5">
            {detailParts.join(' · ')}. This is{' '}
            <strong>not the current budget</strong> — changes cannot be made to historical
            revisions.
          </AlertDescription>
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onCompareWithCurrent}
            className="h-8 text-xs"
          >
            Compare with Current
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onRestoreAsRevision}
            className="h-8 text-xs"
          >
            Restore as New Revision
          </Button>
          <Button
            size="sm"
            onClick={onReturnToCurrent}
            className="h-8 text-xs"
          >
            Return to Current
          </Button>
        </div>
      </div>
    </Alert>
  );
}
